import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import css from './CreativePopupTabs.css';
import { CreativeType } from '../../types';
import { CreativeCardTabs, ANALYSIS_TAB_ID, CREATIVE_TAGS_TAB_ID, TRACK_EVENT_PREFIX, GalleryContext } from '../../consts';
import { GET_CREATIVE_TAGS } from '../../queries';
import { Tabs } from '../../../components/widgets';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { trackMixpanelEvent } from '../../../utils/general';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import CreativeTagsTab from './CreativeTagsTab';
import AnalysisTab from './AnalysisTab';
import { useCreativePerformanceData, useBreakdownChartData } from './hooks';
import { IconSizes } from '../../../components/foundations/Icon';

const CreativePopupTabs = ({
    app,
    source,
    imageHash,
    metrics = {},
    cohorts = {},
    selectedMetrics = [],
    selectedCohorts = [],
    dateRange,
    onHasUnsavedChanges = () => {},
    hasUnsavedChanges = false,
    isLoadingMetrics = false,
    isLoadingCohorts = false,
}) => {
    const [activeTab, setActiveTab] = useState(ANALYSIS_TAB_ID);
    const [pendingTabId, setPendingTabId] = useState();

    const { creativesTags } = useContext(GalleryContext);

    const creativeFetchedTags = useMemo(() => creativesTags?.filter(creativeTag => creativeTag.imageHash === imageHash), [creativesTags, imageHash]);
    const { loading: isLoadingCreativeTags, data: creativeTagsResponse } = useQuery(GET_CREATIVE_TAGS, {
        variables: {
            appId: app,
            imageHashes: [imageHash],
        },
        skip: !!creativeFetchedTags?.length,
    });

    const aiDimensions = useMemo(
        () => creativeFetchedTags || creativeTagsResponse?.creativeTags?.tags || [],
        [creativeFetchedTags, creativeTagsResponse?.creativeTags?.tags]
    );

    const switchTab = tabId => {
        setActiveTab(tabId);
        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Pop Up: Tab Click', { tab: tabId });
    };

    const handleTabClick = tabId => {
        if (hasUnsavedChanges && tabId !== activeTab) {
            setPendingTabId(tabId);
        } else {
            switchTab(tabId);
        }
    };

    const handleConfirmTabSwitch = () => {
        switchTab(pendingTabId);
        closeTabSwitchConfirmation();
    };

    const closeTabSwitchConfirmation = () => {
        setPendingTabId();
    };

    const allOptions = useMemo(
        () => [
            ...selectedMetrics.map(item => ({ ...item, isCohort: false })),
            ...selectedCohorts.map(item => ({ ...item, isCohort: true })),
        ],
        [selectedMetrics, selectedCohorts]
    );

    const { metricOptions, cohortOptions } = useMemo(() => {
        const metricOptions = [];
        const cohortOptions = [];

        allOptions.forEach(option => {
            if (option.isCohort) {
                cohortOptions.push(option);
            } else {
                metricOptions.push(option);
            }
        });

        return {
            metricOptions,
            cohortOptions,
        };
    }, [allOptions]);

    const { loading: performanceLoading, data: performanceChartData } = useCreativePerformanceData({
        app,
        source,
        imageHash,
        metrics: metricOptions,
        cohortFields: cohortOptions,
        dateRange,
    });

    const { loading: breakdownLoading, data: breakdownData, selectedBreakdown, setSelectedBreakdown } = useBreakdownChartData({app, source, imageHash, metrics: metricOptions, cohortFields: cohortOptions, dateRange});

    const chartData = {
        performance: {
            data: performanceChartData,
            loading: performanceLoading,
        },
        breakdown: {
            data: breakdownData,
            loading: breakdownLoading,
            selectedBreakdown,
            setSelectedBreakdown,
        },
    }

    return (
        <div className={css.container}>
            <GeneralPopup
                open={!!pendingTabId}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmTabSwitch}
                onReject={closeTabSwitchConfirmation}
            />
            <Tabs containerClass={css.tabsContainer} selected={activeTab} onClick={handleTabClick} tabs={CreativeCardTabs} iconSize={IconSizes.MEDIUM} />
            {activeTab === CREATIVE_TAGS_TAB_ID && (
                <CreativeTagsTab
                    app={app}
                    source={source}
                    imageHash={imageHash}
                    onHasUnsavedChanges={onHasUnsavedChanges}
                    isLoading={isLoadingCreativeTags}
                    aiDimensions={aiDimensions}
                />
            )}
            {activeTab === ANALYSIS_TAB_ID && (
                <AnalysisTab
                    metrics={metrics}
                    cohorts={cohorts}
                    selectedMetrics={selectedMetrics}
                    selectedCohorts={selectedCohorts}
                    dateRange={dateRange}
                    isLoadingMetrics={isLoadingMetrics}
                    isLoadingCohorts={isLoadingCohorts}
                    chartData={chartData}
                />
            )}
        </div>
    );
};

CreativePopupTabs.propTypes = {
    app: PropTypes.string.isRequired,
    source: PropTypes.string,
    imageHash: CreativeType.imageHash.isRequired,
    metrics: PropTypes.object,
    cohorts: PropTypes.object,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    dateRange: PropTypes.object.isRequired,
    onHasUnsavedChanges: PropTypes.func,
    hasUnsavedChanges: PropTypes.bool,
    isLoadingMetrics: PropTypes.bool,
    isLoadingCohorts: PropTypes.bool,
};

export default CreativePopupTabs;
